<template src="./template.html"></template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import userApi from '@/api/user'
import areaInfoApi from '@/api/areaInfo'
import dateMixin from '@/mixin/dateMixin'
import { monthOptions, dateOptions, hourOptions, minuteOptions } from '@/consts/selectOptions'
import OnetimePaymentMethodInput from '@/components/WashRequestOnetime/components/OnetimePaymentMethodInput.vue'
import {
  lovKeyOnetimePaymentMethodCreditCard,
  lovKeyOnetimePaymentMethodPaid,
  lovKeyOnetimePaymentMethodPaypay
} from '@/consts/lov'
import { ONETIME_MAX_COUNT } from '@/utils/washRequestUtils'
import {
  NINETY_MINUTES_IN_MILLISECONDS,
  TWO_HOURS_IN_MILLISECONDS,
  THREE_HOURS_IN_MILLISECONDS,
  ONE_DAY_IN_MILLISECONDS,
} from '@/consts/time'
import DeliveryDateHelpModal from '@/components/WashRequestOnetime/components/DeliveryDateHelpModal.vue'
import ContactGuidanceRow from '@/components/lib/ContactGuidanceRow.vue'
import {
  useDeliveryType,
  isSameDayAfterCutoff,
  buildInitialDeliveryTypeContext,
} from '@/composables/useDeliveryType'
import { DeliveryType } from '@/consts/washRequest'

function getMinuteOptions(filterValues = [0, 15, 30, 45]) {
  return minuteOptions.filter(option => filterValues.includes(option.val));
}

export default {
  name: 'wash-request',
  components: { OnetimePaymentMethodInput, DeliveryDateHelpModal, ContactGuidanceRow },
  data() {
    return {
      currentStep: 1,
      maxStep: 2,
      isStep1: true,
      isStep2: false,

      masterState: {},
      months: monthOptions,
      dates: dateOptions,
      hours: hourOptions,
      minutes: getMinuteOptions(),
      minutes30: getMinuteOptions([0, 15, 30]),
      minutes0: getMinuteOptions([0]),
      pickupDates: [],
      deliveryDates: [],
      pickupConvHours: hourOptions,
      deliveryConvHours: hourOptions,
      lastPickupConvHours: null,
      lastDeliveryConvHours: null,
      pickupMinutes: getMinuteOptions(),
      deliveryMinutes: getMinuteOptions(),
      areaInfo: [],

      pickupYear: 9999,
      pickupMonth: 1,
      pickupDate: 1,
      pickupHour: 13,
      pickupMinute: 0,
      pickupDt: null,
      deliveryYear: 9999,
      deliveryMonth: 1,
      deliveryDate: 1,
      deliveryHour: 16,
      deliveryMinute: 0,
      deliveryDt: null,
      count: 1,
      deliveryBoxPw: '',
      source_venue_code: '',
      roomNumber: '',
      price: '',
      unitPrice: '',
      onetimePaymentMethodId: '1',
      hasOilTreatment: null,
      zipcode: '',
      businessHolidays: [],
      onetimePayments: [],
      paymentMethodState: {
        paymentMethodId: '1',
        isPending: false,
        additionalInfo: {},
      },

      paymentInfo: {
        url: '',
        formParams: [],
        retUrl: '',
      },
      isValidOnetimeTypeBravo: false,

      showErrMsg1: false,
      showErrMsg2: false,
      showErrMsg3: false,
      showErrMsg4: false,
      showErrMsg5: false,
      showErrorMessagePickupPast: false,
      showErrorSameDayAreaBravo: false,
      showErrorSameDayCutoff: false,
      showErrorSameDayDateMismatch: false,
      showErrorNextDayDateMismatch: false,
      showErrorTwoDaysLaterDateMismatch: false,
      showErrMsgOilTreatmentRequired: false,

      isWaitingResponse: false,
      isDupRequest: false,
      isStatusDisabled: false,
      isStatusWithdrawn: false,
      isStatusStoppedOther: false,
      isDuplicatePaypayPendingQueue: false,
      isServerSameDayUnavailable: false,
      isServerPriceNotFound: false,

      deliveryType: DeliveryType.NEXT_DAY,
      showDeliveryTypeHelp: false,
      deliveryTypeContext: buildInitialDeliveryTypeContext(),

      lovKeyOnetimePaymentMethodPaypay,
    }
  },
  computed: {
    ...mapState('user', {
      // Business Rule No.17 x月はあとy個使えます
      availableRequestCount: state => state.available_request_count,
      isSaturdayAvailable: state => state.is_saturday_available,
      role: state => state.role,
      sendAddrZipcode: state => state.send_addr_zipcode,
      withdrawalDt: state => state.withdrawal_dt,
    }),
    isDeliveryTypeSameDay() {
      return this.deliveryType === DeliveryType.SAME_DAY
    },
    isDeliveryTypeTwoDaysLater() {
      return this.deliveryType === DeliveryType.TWO_DAYS_LATER
    },
    toPickupTime() {
      let now = new Date(new Date().valueOf())
      if (this.isStep2) {
        now = new Date(this.pickupDt.valueOf())
      } else {
        now.setHours(this.pickupHour)
        now.setMinutes(this.pickupMinute)
        now.setSeconds(0)
        now.setMilliseconds(0)
      }
      if (this.isValidOnetimeTypeBravo) {
        return now
      } else if (this.isDeliveryTypeSameDay) {
        return new Date(now.valueOf() + TWO_HOURS_IN_MILLISECONDS)
      } else {
      return new Date(now.valueOf() + NINETY_MINUTES_IN_MILLISECONDS)
      }
    },
    toDeliveryTime() {
      const deliveryTime = new Date(this.deliveryDt.valueOf())
      if (this.isDeliveryTypeSameDay) {
        return new Date(deliveryTime.valueOf() + THREE_HOURS_IN_MILLISECONDS)
      } else {
        return deliveryTime
      }
    },
    addWashRequestConfirmButtonText() {
      return this.paymentMethodState.paymentMethodId === lovKeyOnetimePaymentMethodPaid
        ? this.$t('washRequestOnetime.form.confirmDeliveryRequest')
        : this.$t('washRequestOnetime.form.enterPaymentInfo')
    },
    onetimePaymentMethodName() {
      if (!this.masterState.lovsMap || !this.masterState.lovsMap.onetime_payment_method) {
        return ''
      }
      return this.masterState.lovsMap.onetime_payment_method[this.paymentMethodState.paymentMethodId].val
    },
    counts() {
      return Array.from({ length: ONETIME_MAX_COUNT }, (_, i) => i + 1)
    },
    deliveryTypeOptions() {
      const allTypes = [DeliveryType.SAME_DAY, DeliveryType.NEXT_DAY, DeliveryType.TWO_DAYS_LATER]
      return allTypes.map(type => ({
        value: type,
        label: this.$t(`washRequestOnetime.deliveryTypeLabels.${type}`),
        disabled: !this.isDeliveryTypeAvailable(type),
      }))
    },
    hasNoAvailableDeliveryType() {
      return this.deliveryTypeOptions.every(opt => opt.disabled)
    },
  },
  async mounted() {
    // 休業日取得
    this.businessHolidays = await this.getBusinessHolidays()
    // 単発料金取得
    const { data: onetimePaymentsData } = await userApi.getOnetimePayments()
    this.onetimePayments = onetimePaymentsData

    // 集配日時デフォルト値設定
    this.zipcode = this.sendAddrZipcode
    await this.getMe()
    const obj = {
      'zipcode': this.zipcode,
      'is_onetime': 1
    }
    const { data: areaInfoData } = await areaInfoApi.getAreaInfo(obj)
    const hasError = areaInfoData && areaInfoData['error']
    this.areaInfo = areaInfoData
    this.isValidOnetimeTypeBravo = !hasError && this.areaInfo.is_valid_onetime_b
    this.deliveryTypeContext = useDeliveryType({ now: new Date(), isOnetimeAreaBravo: this.isValidOnetimeTypeBravo })

    this.setPickupHours()
    this.setDeliveryHours()
    if (!hasError) {
      this.lastPickupConvHours = this.pickupConvHours[this.pickupConvHours.length - 1].val
      this.lastDeliveryConvHours = this.deliveryConvHours[this.deliveryConvHours.length - 1].val
    }

    this.deliveryType = this.deliveryTypeContext.defaultType
    this.changeDeliveryType()
    this.setPrice()

    this.masterState = await window.master.$promise
  },
  mixins: [dateMixin],
  methods: {
    async getMe() {
      await this.$store.dispatch('user/getMe')
        .then(me => {
          if(!this.zipcode) {
            this.zipcode = me.send_addr_zipcode
          }
          if (me.source_venue_code) {
            this.source_venue_code = me.source_venue_code
            this.roomNumber = me.send_addr_detail3.includes('@') ? me.send_addr_detail3.trim().split('@')[1] : ''
          }
      })
    },
    initPickupDeliveryDateTime() {
      let pickupDt = new Date(new Date().valueOf())
      const minuteInterval = this.isValidOnetimeTypeBravo ? 30 : 15
      const remainder = pickupDt.getMinutes() % minuteInterval
      if (remainder > 0) {
        pickupDt = new Date(pickupDt.valueOf() + 60 * (minuteInterval - remainder) * 1000)
      }

      // プラスした時間が当日中最終選択時間よりあとの場合はデフォルトの時間を翌日にするために更に時間をプラスする
      if (pickupDt.getHours() > this.lastPickupConvHours
        || (pickupDt.getHours() == this.lastPickupConvHours && pickupDt.getMinutes() > this.minutes30[this.minutes30.length - 1].val)) {
        const to24 = 24 - pickupDt.getHours()
        pickupDt = new Date(pickupDt.valueOf() + 3600 * to24 * 1000)
      }

      const firstPickupConvHour = this.pickupConvHours[0].val
      if (firstPickupConvHour > pickupDt.getHours()) {
        pickupDt.setHours(firstPickupConvHour)
        pickupDt.setMinutes(0)
      }

      pickupDt = this.getAvailableDate(pickupDt, this.businessHolidays, this.isSaturdayAvailable)
      this.pickupYear = pickupDt.getFullYear()
      this.pickupMonth = pickupDt.getMonth() + 1
      this.pickupDate = pickupDt.getDate()
      this.pickupHour = pickupDt.getHours()
      this.pickupMinute = pickupDt.getMinutes()
      if (this.lastPickupConvHours) {
        if ((pickupDt.getHours() > this.lastPickupConvHours)
          || (pickupDt.getHours() == this.lastPickupConvHours && pickupDt.getMinutes() > this.minutes[this.minutes.length - 2])) {
          this.pickupHour = this.lastPickupConvHours
          this.pickupMinute = 30
        }
      }

      const deliveryDt = this.resolveDeliveryDt(pickupDt, 1)
      this.deliveryYear = deliveryDt.getFullYear()
      this.deliveryMonth = deliveryDt.getMonth() + 1
      this.deliveryDate = deliveryDt.getDate()
      this.deliveryHour = deliveryDt.getHours()
      if (this.lastDeliveryConvHours) {
        if (deliveryDt.getHours() > this.lastDeliveryConvHours) {
          this.deliveryHour = this.lastDeliveryConvHours
          this.deliveryMinute = 0
        } else {
          this.deliveryMinute = deliveryDt.getMinutes()
        }
      }

      this.setPickupDates()
      this.setDeliveryDates()
      this.setPickupMinutes()
      this.setDeliveryMinutes()
    },
    resolveDeliveryDt(pickupDt, daysOffset) {
      let deliveryDt = new Date(pickupDt.valueOf() + ONE_DAY_IN_MILLISECONDS * daysOffset)
      const firstDeliveryConvHour = this.deliveryConvHours[0].val
      if (firstDeliveryConvHour > pickupDt.getHours()) {
        deliveryDt.setHours(firstDeliveryConvHour)
        deliveryDt.setMinutes(0)
      }
      deliveryDt = this.getAvailableDate(deliveryDt, this.businessHolidays)
      const workingDay = this.getWorkingDay(pickupDt, deliveryDt, this.businessHolidays, this.isSaturdayAvailable)
      if (workingDay < 2) {
        deliveryDt.setDate(deliveryDt.getDate() + (2 - workingDay))
      }
      return deliveryDt
    },
    changeDeliveryType() {
      this.initPickupDeliveryDateTime()

      if (this.isDeliveryTypeSameDay) {
        this.pickupHour = 12
        this.pickupMinute = 0
        this.deliveryHour = 18
        this.deliveryMinute = 0
        this.deliveryDate = this.pickupDate
      } else if (this.isDeliveryTypeTwoDaysLater) {
        const pickupDt = new Date(this.pickupYear, this.pickupMonth - 1, this.pickupDate, this.pickupHour, this.pickupMinute)
        const deliveryDt = this.resolveDeliveryDt(pickupDt, 2)
        this.deliveryYear = deliveryDt.getFullYear()
        this.deliveryMonth = deliveryDt.getMonth() + 1
        this.deliveryDate = deliveryDt.getDate()
        this.deliveryHour = deliveryDt.getHours()
        if (this.lastDeliveryConvHours) {
          if (deliveryDt.getHours() > this.lastDeliveryConvHours) {
            this.deliveryHour = this.lastDeliveryConvHours
            this.deliveryMinute = 0
          } else {
            this.deliveryMinute = deliveryDt.getMinutes()
          }
        }
      }

      this.setPrice()
    },
    changePickupMonth() {
      this.setPickupDates()
      this.setPrice()
    },
    changePickupHour() {
      this.setPickupMinutes()
      this.setPrice()
    },
    changeDeliveryMonth() {
      this.setDeliveryDates()
      this.setPrice()
    },
    changeDeliveryHour() {
      this.setDeliveryMinutes()
      this.setPrice()
    },
    nextStep() {
      if (!this.checkCurrentStep()) { return }
      this.currentStep = Math.min(this.currentStep + 1, this.maxStep)
      this.updateStep()
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 0)
    },
    prevStep() {
      this.currentStep = Math.max(this.currentStep - 1, 1)
      this.updateStep()
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 0)
    },
    updateStep() {
      for (let i = 1; i <= this.maxStep; i++) {
        const flag = i === this.currentStep
        this[`isStep${i}`] = flag
      }
    },
    checkCurrentStep() {
      return this[`checkStep${this.currentStep}`]()
    },
    setPickupDates() {
      // 対象年月から休業日を除く日付を取得
      const yearAdjust = this.isNextYear(this.pickupYear, this.pickupMonth) ? 1 : 0
      this.pickupDates = this.getWeekDaysList(this.pickupYear + yearAdjust, this.pickupMonth, this.businessHolidays, this.isSaturdayAvailable)
      if (this.pickupDates.indexOf(this.pickupDate) === -1) {
        const pickupDt = new Date(
          this.pickupYear + yearAdjust, this.pickupMonth - 1, this.pickupDates[0],
          this.pickupHour, this.pickupMinute, 0)
        this.pickupDt = this.getAvailableDate(pickupDt, this.businessHolidays, this.isSaturdayAvailable)
        this.pickupDate = this.pickupDt.getDate()
      }
    },
    setDeliveryDates() {
      // 対象年月から休業日を除く日付を取得
      const yearAdjust = this.isNextYear(this.deliveryYear, this.deliveryMonth) ? 1 : 0
      this.deliveryDates = this.getWeekDaysList(this.deliveryYear + yearAdjust, this.deliveryMonth, this.businessHolidays, this.isSaturdayAvailable)
      if (this.deliveryDates.indexOf(this.deliveryDate) === -1) {
        const deliveryDt = new Date(
          this.deliveryYear + yearAdjust, this.deliveryMonth - 1, this.deliveryDates[0],
          this.deliveryHour, this.deliveryMinute, 0)
        this.deliveryDt = this.getAvailableDate(deliveryDt, this.businessHolidays, this.isSaturdayAvailable)
        this.deliveryDate = this.deliveryDt.getDate()
      }
    },
    setPickupHours() {
      const pickupStartHour = this.isValidOnetimeTypeBravo ? parseInt(this.areaInfo.start_time.split(':')[0], 10) : 13
      const pickupEndHour = this.isValidOnetimeTypeBravo ? parseInt(this.areaInfo.end_time.split(':')[0], 10) : 20
      this.pickupConvHours = hourOptions.filter(v => v.val >= pickupStartHour && v.val <= pickupEndHour)
    },
    setPickupMinutes() {
      if (this.lastPickupConvHours === this.pickupHour) {
        if (this.isValidOnetimeTypeBravo) {
          // 単発エリアでは20時を選択した場合、00分まで選択可能とする
          this.pickupMinute = 0
          this.pickupMinutes = this.minutes0
        } else if (this.lastPickupConvHours === this.pickupHour) {
          // 20時を選択した場合、30分まで選択可能とする
          this.pickupMinute = Math.min(this.pickupMinute, 30)
          this.pickupMinutes = this.minutes30
        }
      } else {
        this.pickupMinutes = this.isValidOnetimeTypeBravo ? getMinuteOptions([0, 30]) : this.minutes
      }
    },
    setDeliveryHours() {
      const deliveryStartHour = this.isValidOnetimeTypeBravo
        ? parseInt(this.areaInfo.start_time.split(':')[0], 10)
        : 16
      const deliveryEndHour = this.isValidOnetimeTypeBravo ? parseInt(this.areaInfo.end_time.split(':')[0], 10) : 22
      this.deliveryConvHours = hourOptions.filter(v => v.val >= deliveryStartHour && v.val <= deliveryEndHour)
    },
    setDeliveryMinutes() {
      if (this.lastDeliveryConvHours === this.deliveryHour) {
        // 22時を選択した場合、00分まで選択可能とする
        this.deliveryMinute = Math.min(this.deliveryMinute, 0)
        this.deliveryMinutes = this.minutes0
      } else {
        this.deliveryMinutes = this.isValidOnetimeTypeBravo ? getMinuteOptions([0, 30]) : this.minutes
      }
    },
    buildPickupDeliveryDt() {
      const pickupYearAdjust = this.isNextYear(this.pickupYear, this.pickupMonth) ? 1 : 0
      const pickupDt = new Date(
        this.pickupYear + pickupYearAdjust, this.pickupMonth - 1, this.pickupDate,
        this.pickupHour, this.pickupMinute, 0
      )
      const deliveryYearAdjust = this.isNextYear(this.deliveryYear, this.deliveryMonth) ? 1 : 0
      const deliveryDt = new Date(
        this.deliveryYear + deliveryYearAdjust, this.deliveryMonth - 1, this.deliveryDate,
        this.deliveryHour, this.deliveryMinute, 0
      )
      return { pickupDt, deliveryDt }
    },
    setPrice() {
      const { pickupDt, deliveryDt } = this.buildPickupDeliveryDt()
      this.pickupDt = pickupDt
      this.deliveryDt = deliveryDt

      if (this.areaInfo['error'] || isNaN(pickupDt.valueOf())) {
        this.price = ''
        this.unitPrice = ''
        return
      }

      const week = pickupDt.getDay()
      const time = pickupDt.getHours() + ':00:00'
      const onetimePayment = this.onetimePayments.find(v => v.wreq_time === time && v.delivery_type === this.deliveryType)
      if (!onetimePayment) {
        this.price = ''
        this.unitPrice = ''
        return
      }

      this.price = this.count * onetimePayment[`week${week}`]
      this.unitPrice = this.price / this.count
    },
    checkStep1() {
      this.resetLocalErrMsgs()
      this.resetServerErrMsgs()

      // 提供エリア外の場合は依頼不可
      if (this.areaInfo['error']) {
        this.showErrMsg4 = true
        return false
      }

      // 退会日が過ぎている場合は依頼不可
      const now = new Date()
      if (this.withdrawalDt) {
        let withdrawalDt = new Date(this.withdrawalDt)
        if (now.getTime() > withdrawalDt.getTime()) {
          this.isStatusWithdrawn = true
          return false
        }
      }

      const { pickupDt, deliveryDt } = this.buildPickupDeliveryDt()

      if (isNaN(pickupDt.valueOf()) || isNaN(deliveryDt.valueOf())) {
        this.showErrMsg1 = true
        return false
      }

      // 集荷日時が現在時刻より前かチェック
      if (pickupDt.valueOf() < now.valueOf()) {
        this.showErrorMessagePickupPast = true
        return false
      }

      if (this.isDeliveryTypeSameDay && this.deliveryTypeContext.isSameDayDisabled) {
        this.showErrorSameDayAreaBravo = true
        return false
      }

      if (this.isDeliveryTypeSameDay && isSameDayAfterCutoff(pickupDt, now)) {
        this.showErrorSameDayCutoff = true
        return false
      }

      if (this.isDeliveryTypeSameDay && pickupDt.getDay() !== deliveryDt.getDay()) {
        this.showErrorSameDayDateMismatch = true
        return false
      }

      const workingDay = this.getWorkingDay(pickupDt, deliveryDt, this.businessHolidays, this.isSaturdayAvailable)
      if (this.deliveryType === DeliveryType.NEXT_DAY) {
        // 配達日時に集荷日時の翌営業日より後を指定している場合はエラー
        if (workingDay > 2) {
          this.showErrorNextDayDateMismatch = true
          return false
        }
      }

      if (this.deliveryType === DeliveryType.TWO_DAYS_LATER) {
        // 配達日時に集荷日時の当日または翌営業日を指定している場合はエラー
        if (workingDay < 3) {
          this.showErrorTwoDaysLaterDateMismatch = true
          return false
        }
      }

      if (!this.isDeliveryTypeSameDay) {
        // 配達時間は最短でも集荷時間の24時間後以降しか選べない
        const minDiff = ONE_DAY_IN_MILLISECONDS
        const result = deliveryDt.valueOf() - pickupDt.valueOf() >= minDiff
        if (!result) {
          this.showErrMsg2 = true
          return false
        }

        // かつ配達時間は休業日を除く24時間後しか依頼できない
        if (workingDay < 2) {
          this.showErrMsg2 = true
          return false
        }
      }

      // Business Rule No.4.1 配達時間は最長で30日後まで選択可能
      let deliveryMaxDt = new Date(now.valueOf() + 3600*24*30*1000)
      if (deliveryDt > deliveryMaxDt) {
        this.showErrMsg3 = true
        return false
      }

      // 選択された集荷日時が提供不可の場合は依頼不可
      if (this.price === 0 || isNaN(this.price)) {
        this.showErrMsg5 = true
        return false
      }

      if (this.hasOilTreatment === null) {
        this.showErrMsgOilTreatmentRequired = true
        return false
      }

      this.pickupDt = pickupDt
      this.deliveryDt = deliveryDt
      return true
    },
    checkStep2() {
      return true
    },
    async addWashRequest() {
      if (!this.checkStep1()) { return }
      if (this.isWaitingResponse) { return }

      this.resetServerErrMsgs()

      const dtFormat = Vue.filter('dtFormat')
      const reqData = {
        sched_pickup_dt_from: dtFormat(this.pickupDt, 'yyyy-mm-dd HH:MM:SS'),
        sched_pickup_dt: dtFormat(this.toPickupTime, 'yyyy-mm-dd HH:MM:SS'),
        sched_delivery_dt: dtFormat(this.toDeliveryTime, 'yyyy-mm-dd HH:MM:SS'),
        count: this.count,
        delivery_box_pw: this.deliveryBoxPw,
        room_number: this.roomNumber,
        has_oil_treatment: this.hasOilTreatment,
        // サーバーを1台ずつ更新している間は、古いコードのサーバーがこのキーを直接読むため、空で送る。
        // wash_requests.bikou の列を削除するときに合わせて削除する
        bikou: '',
      }
      // Paypay用の追加情報
      if (this.paymentMethodState.paymentMethodId === lovKeyOnetimePaymentMethodPaypay) {
        reqData.redirect_url = `${window.location.origin}/onetime-wash-request-complete`
      }
      // Paid用の追加情報
      if (this.paymentMethodState.paymentMethodId === lovKeyOnetimePaymentMethodPaid) {
        reqData.frd_key = this.paymentMethodState.additionalInfo.frdKey
      }

      this.isWaitingResponse = true
      try {
        let apiFunction
        if (this.paymentMethodState.paymentMethodId === lovKeyOnetimePaymentMethodCreditCard) {
          apiFunction = userApi.addOnetimeWashRequestForCredit
        } else if (this.paymentMethodState.paymentMethodId === lovKeyOnetimePaymentMethodPaypay) {
          apiFunction = userApi.addOnetimeWashRequestForPaypay
        } else if (this.paymentMethodState.paymentMethodId === lovKeyOnetimePaymentMethodPaid) {
          apiFunction = userApi.addOnetimeWashRequestForPaid
        } else {
          throw new Error(`unsupported payment method: ${this.paymentMethodState.paymentMethodId}`)
        }
        const { data } = await apiFunction(reqData)
        if (this.paymentMethodState.paymentMethodId === lovKeyOnetimePaymentMethodCreditCard) {
          this.afterAddWashRequestForPaymentMethodCredit(data)
        } else if (this.paymentMethodState.paymentMethodId === lovKeyOnetimePaymentMethodPaypay) {
          this.afterAddWashRequestForPaymentMethodPaypay(data)
        } else if (this.paymentMethodState.paymentMethodId === lovKeyOnetimePaymentMethodPaid) {
          this.afterAddWashRequestForPaymentMethodPaid()
        }
      } catch (err) {
        if (err.response.data.reason === 'dup') {
          this.isDupRequest = true
        }
        if (err.response.data.reason === 'status_disabled') {
          this.isStatusDisabled = true
        }
        if (err.response.data.reason === 'status_withdrawn') {
          this.isStatusWithdrawn = true
          await this.$store.dispatch('localStorage/set', {
            key: 'serverToken',
            val: null,
          })
        }
        if (err.response.data.reason === 'status_stopped_other') {
          this.isStatusStoppedOther = true
        }
        if (err.response.data.reason === 'duplicate_paypay_pending_queue') {
          this.isDuplicatePaypayPendingQueue = true
        }
        const sameDayErrorReasons = [
          'sameDayDateMismatch', 'sameDayAreaOutside', 'sameDayCutoff',
          'sameDaySunday', 'sameDayHoliday',
        ]
        if (sameDayErrorReasons.includes(err.response.data.reason)) {
          this.isServerSameDayUnavailable = true
        }
        if (err.response.data.reason === 'price_not_found') {
          this.isServerPriceNotFound = true
        }
      } finally {
        this.isWaitingResponse = false
      }
    },
    afterAddWashRequestForPaymentMethodCredit(responseData) {
      this.updatePaymentFormValues(responseData.payment)
      setTimeout(() => {
        const form = document.querySelector('#payment-form')
        form.submit()
      }, 0)
    },
    updatePaymentFormValues(obj) {
      this.paymentInfo.url = obj.url
      this.paymentInfo.formParams = obj.form_params
      this.paymentInfo.retUrl = `${location.protocol}//${location.host}/${obj.ret_url_path}`
    },
    afterAddWashRequestForPaymentMethodPaypay(responseData) {
      window.location.href = responseData.url;
    },
    afterAddWashRequestForPaymentMethodPaid() {
      this.$router.push({ name: 'onetime-wash-request-complete' })
    },
    resetLocalErrMsgs() {
      this.showErrMsg1 = false
      this.showErrMsg2 = false
      this.showErrMsg3 = false
      this.showErrMsg4 = false
      this.showErrMsg5 = false
      this.showErrorMessagePickupPast = false
      this.showErrorSameDayAreaBravo = false
      this.showErrorSameDayCutoff = false
      this.showErrorSameDayDateMismatch = false
      this.showErrorNextDayDateMismatch = false
      this.showErrorTwoDaysLaterDateMismatch = false
      this.showErrMsgOilTreatmentRequired = false
    },
    resetServerErrMsgs() {
      this.isDupRequest = false
      this.isStatusDisabled = false
      this.isStatusWithdrawn = false
      this.isStatusStoppedOther = false
      this.isDuplicatePaypayPendingQueue = false
      this.isServerSameDayUnavailable = false
      this.isServerPriceNotFound = false
    },
    onPaymentMethodStateChange(paymentMethodState) {
      this.paymentMethodState = paymentMethodState
    },
    onClickDeliveryTypeHelp() {
      this.showDeliveryTypeHelp = true
    },
    isDeliveryTypeAvailable(type) {
      return this.deliveryTypeContext.availableTypes.includes(type)
    },
  }
}
</script>

<style lang="scss" src="./style.scss" scoped>
</style>
